import * as Yup from 'yup';

import { SendReceiptType } from 'types';
import { phoneNumber } from 'utils/validation';
import { EventCheckout } from 'store/api/event';
import { isGenericCardholderName } from 'utils/helpers';

import { FormValues } from './types';

const extractContacts = (customer?: {
  additionalEmails?: string[];
  additionalPhones?: string[];
  person?: {
    email?: string;
    phone?: string;
  };
}) => {
  const emails: string[] = [
    customer?.person?.email || '',
    ...(customer?.additionalEmails ?? []),
  ];

  const phoneNumbers: string[] = [
    customer?.person?.phone || '',
    ...(customer?.additionalPhones ?? []),
  ];

  return { emails, phoneNumbers };
};

export const getEmailsAndPhoneNumbers = (data?: EventCheckout) => {
  const initialValues = { emails: [''], phoneNumbers: [''] };

  if (!data) {
    return initialValues;
  }

  if (data.customer) {
    if (isGenericCardholderName(data.customer?.person?.name)) {
      return initialValues;
    }

    return extractContacts(data.customer);
  }

  if (data.syncedCustomers?.length) {
    const emails: string[] = [];
    const phoneNumbers: string[] = [];

    data.syncedCustomers.forEach((customer) => {
      if (isGenericCardholderName(customer?.person?.name)) {
        return initialValues;
      }

      const contacts = extractContacts(customer);
      emails.push(...contacts.emails);
      phoneNumbers.push(...contacts.phoneNumbers);
    });

    return {
      emails: Array.from(new Set(emails)),
      phoneNumbers: Array.from(new Set(phoneNumbers)),
    };
  }

  return initialValues;
};

export const getInitialValues = (data?: EventCheckout): FormValues => {
  const { emails, phoneNumbers } = getEmailsAndPhoneNumbers(data);

  return {
    emails,
    phoneNumbers,
    hasContactInfo: false,
    receiptType: SendReceiptType.Default,
  };
};

export const validationSchema = Yup.object().shape({
  receiptType: Yup.string().required(),
  phoneNumbers: Yup.array().of(phoneNumber('phoneNumbers')),
  emails: Yup.array().min(1).of(Yup.string().email('Email must be a valid')),
  hasContactInfo: Yup.boolean()
    .when(['emails', 'phoneNumbers'], {
      otherwise: (schema) => schema,
      then: (schema) =>
        schema.oneOf(
          [true],
          'At least one email or phone number must be provided'
        ),
      is: (emails: string[], phoneNumbers: string[]) => {
        const hasEmail = !!emails.filter((email) => email && email.length > 0)
          .length;
        const hasPhoneNumber = !!phoneNumbers.filter(
          (phone) => phone && phone.length
        ).length;

        return !(hasEmail || hasPhoneNumber);
      },
    })
    .required(),
});
